var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var SUPPORTED_AUTH_TYPE;
(function (SUPPORTED_AUTH_TYPE) {
    // Marketing API
    SUPPORTED_AUTH_TYPE["MAPI"] = "MAPI";
    // TikTok Accounts API
    SUPPORTED_AUTH_TYPE["TTAAPI"] = "TTAAPI";
    // Creator Marketplace API
    SUPPORTED_AUTH_TYPE["CMAPI"] = "CMAPI";
})(SUPPORTED_AUTH_TYPE || (SUPPORTED_AUTH_TYPE = {}));
const ADS_TIKTOK_COM = "https://ads.tiktok.com";
const GRACE_PERIOD_SECONDS = 10;
const TIMEOUT_MS = 3000;
export const ERROR_MSG = {
    getSessionToken: {
        failed: "failed to get session token. please try again later",
        timeout: "request timeout",
    },
    redirectToAuth: {
        unsupported: `Unsupported auth type. Please select from: ${Object.keys(SUPPORTED_AUTH_TYPE)
            .join(", ")
            .replace(/, ([^,]*)$/, " and $1")}`, // replace the last ", " with "and"
    },
    requestRedirectionTo: {
        invalidURL: "Invalid URL",
        failed: "Request failed",
        notAllowed: "This URL is not allowed for redirection",
    },
    pending: "Please wait for another async method to return before trying again.",
};
export default class App {
    constructor(appID) {
        /**
         * To avoid race conditions, only one data request will be processed at a time
         */
        this.requestDataFromParent = (messageToParent, failReason) => __awaiter(this, void 0, void 0, function* () {
            if (this.listenerPendingLock) {
                throw new Error(ERROR_MSG.pending);
            }
            this.listenerPendingLock = true;
            dispatchToParent(messageToParent);
            try {
                const result = yield listenWithTimeout(failReason);
                return result;
            }
            finally {
                this.listenerPendingLock = false;
            }
        });
        this.getSessionToken = () => __awaiter(this, void 0, void 0, function* () {
            if (this.sessionToken) {
                try {
                    const exp = JSON.parse(atob(this.sessionToken.split(".")[1])).exp;
                    if (new Date() <= new Date((exp - GRACE_PERIOD_SECONDS) * 1000)) {
                        return this.sessionToken;
                    }
                }
                catch (_a) {
                    // continue if no valid local cache
                }
            }
            this.sessionToken = yield this.requestDataFromParent({ appID: this.appID, action: "getSessionToken" }, ERROR_MSG.getSessionToken.failed);
            return this.sessionToken;
        });
        this.redirectToAuth = (authType) => {
            if (!(authType in SUPPORTED_AUTH_TYPE)) {
                throw new Error(ERROR_MSG.redirectToAuth.unsupported);
            }
            dispatchToParent({ action: "redirectAuth", type: authType });
        };
        this.requestRedirectionTo = (targetUrlStr) => __awaiter(this, void 0, void 0, function* () {
            try {
                const targetUrl = new URL(targetUrlStr);
                const msg = yield this.requestDataFromParent({
                    action: "requestRedirectionTo",
                    targetUrlStr: targetUrl.href,
                }, ERROR_MSG.requestRedirectionTo.failed);
                if (msg === true) {
                    return true;
                }
                if (msg === "not_allowed") {
                    throw new Error(ERROR_MSG.requestRedirectionTo.notAllowed);
                }
                return false;
            }
            catch (e) {
                if (e instanceof TypeError && e.message === "Invalid URL") {
                    throw new Error(ERROR_MSG.requestRedirectionTo.invalidURL);
                }
                else {
                    throw e;
                }
            }
        });
        this.getAppCenterURL = () => {
            return "https://ads.tiktok.com/app-center/apps/" + this.appID;
        };
        this.appID = appID;
        this.sessionToken = "";
        this.listenerPendingLock = false;
    }
}
function dispatchToParent(message) {
    window.parent.postMessage(message, ADS_TIKTOK_COM);
}
function listenWithTimeout(errorMsg) {
    let messageListener;
    return Promise.race([
        new Promise((resolve, reject) => {
            messageListener = (event) => {
                // Ensure the message is from a trusted source (e.g., same origin)
                if (event.origin !== ADS_TIKTOK_COM) {
                    console.log("unexpected message source");
                    return;
                }
                if (!event.data) {
                    reject(new Error(errorMsg));
                }
                else {
                    resolve(event.data);
                }
            };
            window.addEventListener("message", messageListener);
        }),
        new Promise((_, reject) => setTimeout(reject, TIMEOUT_MS, new Error(ERROR_MSG.getSessionToken.timeout))),
    ])
        .then((value) => {
        if (messageListener) {
            window.removeEventListener("message", messageListener);
        }
        return Promise.resolve(value);
    })
        .catch((reason) => {
        if (messageListener) {
            window.removeEventListener("message", messageListener);
        }
        return Promise.reject(reason);
    });
}
